import React, { useState, useEffect, useRef } from 'react';
import { FiCalendar, FiFileText, FiRepeat, FiCheckCircle, FiDownload, FiSearch, FiX } from 'react-icons/fi';
import styled, { keyframes } from 'styled-components';
import { MdOutlineNumbers } from 'react-icons/md';
import api from '../services/api';
import { toast } from 'react-toastify';
import {
  ModalBody,
  FilterGroup,
  FilterLabel,
  FilterBadges,
  FilterBadge,
  FilterInput,
  FilterSectionHeader,
  SuggestionsContainer,
  SuggestionItem,
  SubmitButton,
} from './XmlDownloadModalStyles';
import XmlDownloadTaskLog from './XmlDownloadTaskLog';

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const DlProgressWrap = styled.div`
  width: 100%; height: 6px;
  background: rgba(255,255,255,0.08);
  border-radius: 3px; overflow: hidden; margin: 0.6em 0 0.3em;
  &::after {
    content: ''; display: block; height: 100%;
    width: ${({ $p }) => $p}%;
    background: ${({ $done }) => $done
      ? '#10b981'
      : 'linear-gradient(90deg, #10b981aa, #10b981, #10b981aa)'};
    background-size: ${({ $done }) => $done ? 'auto' : '200% 100%'};
    animation: ${({ $done }) => $done ? 'none' : shimmer} 1.4s linear infinite;
    border-radius: 3px; transition: width 0.3s ease;
  }
`;

const DlLabel = styled.div`
  display: flex; justify-content: space-between;
  font-size: 0.72em; color: inherit; opacity: 0.6; margin-bottom: 0.6em;
`;

const BtnsRow = styled.div`
  display: flex; gap: 0.6em; flex-wrap: wrap; width: 100%;
`;

const DlBtn = styled.button`
  display: flex; align-items: center; justify-content: center; gap: 0.5em;
  padding: 0.75em 1.1em; font-size: 0.85em; font-weight: 500;
  border-radius: 12px; cursor: pointer; transition: all 0.2s;
  white-space: nowrap; flex: 1;
  background: ${({ $primary }) => $primary ? '#10b981' : 'rgba(255,255,255,0.06)'};
  color: ${({ $primary }) => $primary ? '#fff' : 'inherit'};
  border: 1px solid ${({ $primary }) => $primary ? '#10b981' : 'rgba(255,255,255,0.12)'};
  &:hover { opacity: 0.85; transform: translateY(-1px); }
  &:disabled { opacity: 0.3; cursor: not-allowed; transform: none; }
`;

export default function XmlDownloadFilterTab({ onClose }) {
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [modelo, setModelo] = useState([]);
  const [cfopList, setCfopList] = useState([]);
  const [cfopInput, setCfopInput] = useState('');
  const [cfopSuggestions, setCfopSuggestions] = useState([]);
  const [allCfops, setAllCfops] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [direcao, setDirecao] = useState(null);
  const [status, setStatus] = useState(null);

  const [isProcessing, setIsProcessing] = useState(false);
  const [processingComplete, setProcessingComplete] = useState(false);
  const [taskLogs, setTaskLogs] = useState([]);
  const [taskProgress, setTaskProgress] = useState(0);
  const [currentStage, setCurrentStage] = useState('');
  const [taskStats, setTaskStats] = useState({
    totalChaves: 0,
    arquivosEncontrados: 0,
    arquivosFaltantes: 0,
  });
  const [outputId, setOutputId] = useState(null);
  const [dlState, setDlState] = useState(null); // { progress, done, rate }

  const wsRef = useRef(null);
  const cfopInputRef = useRef(null);
  const pollRef = useRef(null);
  const outputIdRef = useRef(null);

  useEffect(() => {
    fetch('/cfops.json')
      .then((r) => r.json())
      .then((data) => setAllCfops(data.list || []))
      .catch(() => {});
  }, []);

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString('pt-BR');
    setTaskLogs((prev) => [...prev, { message, type, timestamp }]);
  };

  const toggleModelo = (value) => {
    setModelo((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const handleCfopInput = (value) => {
    setCfopInput(value);
    if (value.length >= 1) {
      const filtered = allCfops.filter(
        (c) =>
          c.codigo.startsWith(value) ||
          c.descricao.toLowerCase().includes(value.toLowerCase())
      );
      setCfopSuggestions(filtered.slice(0, 8));
      setShowSuggestions(true);
    } else {
      setCfopSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const addCfop = (codigo) => {
    if (!cfopList.includes(codigo)) {
      setCfopList((prev) => [...prev, codigo]);
    }
    setCfopInput('');
    setCfopSuggestions([]);
    setShowSuggestions(false);
    cfopInputRef.current?.focus();
  };

  const removeCfop = (codigo) => {
    setCfopList((prev) => prev.filter((c) => c !== codigo));
  };

  const handleCfopKeyDown = (e) => {
    if (e.key === 'Enter' && cfopInput.trim()) {
      addCfop(cfopInput.trim());
    }
    if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const hasFilters =
    dataInicio || dataFim || modelo.length > 0 || cfopList.length > 0 || direcao || status;

  const startFallbackPoll = (oid) => {
    // Delay inicial: aguarda 10s antes da primeira tentativa (arquivo ainda não existe)
    const MAX_ATTEMPTS = 72; // 72 * 5s = 6 min máximo
    let attempts = 0;

    const doPoll = async () => {
      if (!pollRef.current) return; // cancelado
      attempts++;
      try {
        const res = await api.get(`/xml-download/download-filter-report?outputId=${oid}`, { responseType: 'blob' });
        const text = await res.data.text();
        const rel = JSON.parse(text);
        if (rel && rel.estatisticas) {
          clearTimeout(pollRef.current);
          pollRef.current = null;
          const { totalFiltradas, encontradasEmDisco, faltantesEmDisco } = rel.estatisticas;
          setTaskProgress(100);
          setCurrentStage('Concluído');
          setTaskStats({ totalChaves: totalFiltradas, arquivosEncontrados: encontradasEmDisco, arquivosFaltantes: faltantesEmDisco });
          addLog(`Concluído: ${encontradasEmDisco} encontrados, ${faltantesEmDisco} faltantes.`, 'success');
          setProcessingComplete(true);
          setIsProcessing(false);
          toast.success('Download de XMLs por filtros concluído!');
          return;
        }
      } catch (e) {
        // 404 = ainda processando, ignora silenciosamente
        if (e?.response?.status !== 404) {
          console.warn('Poll error:', e?.message);
        }
      }
      if (attempts >= MAX_ATTEMPTS) {
        pollRef.current = null;
        setIsProcessing(false);
        addLog('Timeout aguardando resposta do servidor.', 'error');
        return;
      }
      pollRef.current = setTimeout(doPoll, 5000);
    };

    // Primeira tentativa só após 10s
    pollRef.current = setTimeout(doPoll, 10000);
  };

  const connectWebSocket = (projectId) => {
    const authData = JSON.parse(localStorage.getItem('vidal_auth') || '{}');
    const token = authData.token;
    if (!token) {
      addLog('Erro: Token de autenticação não encontrado', 'error');
      return;
    }

    const ws = new WebSocket(`wss://log.vidal-app.com/ws?token=${token}`);
    wsRef.current = ws;

    ws.onopen = () => {
      addLog('Conexão estabelecida com o servidor', 'info');
      ws.send(JSON.stringify({
        action: 'subscribe',
        projectId,
        type: 'download_xml_filter',
      }));
    };

    ws.onmessage = (event) => {
      try {
        const parsed = JSON.parse(event.data);
        if (!parsed.success || !parsed.message) return;

        // WS server entrega: { success: true, message: { type, projectId, data: {...} } }
        const msg = parsed.message;
        if (msg.type !== 'download_xml_filter' || msg.projectId !== projectId) return;

        const messageData = msg.data;
        if (!messageData) return;

        const { progress, stage, totalChaves, arquivosEncontrados, arquivosFaltantes, message, status: msgStatus } = messageData;

        if (progress !== undefined && progress !== null) setTaskProgress(progress);
        if (stage) setCurrentStage(stage);
        if (totalChaves !== undefined) setTaskStats((p) => ({ ...p, totalChaves }));
        if (arquivosEncontrados !== undefined) setTaskStats((p) => ({ ...p, arquivosEncontrados }));
        if (arquivosFaltantes !== undefined) setTaskStats((p) => ({ ...p, arquivosFaltantes }));

        if (message) {
          const logType = msgStatus === 'error' ? 'error' : msgStatus === 'completed' ? 'success' : 'info';
          addLog(message, logType);
        }

        if (msgStatus === 'completed' || progress === 100) {
          clearTimeout(pollRef.current);
          pollRef.current = null;
          setProcessingComplete(true);
          setIsProcessing(false);
          addLog('Processamento concluído!', 'success');
          toast.success('Download de XMLs por filtros concluído!');
        }

        if (msgStatus === 'error') {
          setIsProcessing(false);
          toast.error(message || 'Erro no processamento');
        }
      } catch {}
    };

    ws.onerror = () => addLog('Erro na conexão WebSocket', 'error');
    ws.onclose = () => addLog('Conexão WebSocket encerrada', 'info');
  };

  const handleSubmit = async () => {
    const projectId = localStorage.getItem('vidal_selected_project');
    if (!projectId) {
      toast.error('Nenhum projeto selecionado');
      return;
    }
    if (!hasFilters) {
      toast.error('Selecione ao menos um filtro');
      return;
    }

    setIsProcessing(true);
    setProcessingComplete(false);
    setTaskLogs([]);
    setTaskProgress(0);
    setCurrentStage('Iniciando...');
    setTaskStats({ totalChaves: 0, arquivosEncontrados: 0, arquivosFaltantes: 0 });

    try {
      // Conectar WS antes do POST para não perder mensagens de tarefas rápidas
      await new Promise((resolve) => {
        connectWebSocket(projectId);
        const ws = wsRef.current;
        if (!ws) return resolve();
        const timer = setTimeout(resolve, 3000);
        ws.onopen = (orig => (e) => { orig && orig(e); clearTimeout(timer); resolve(); })(ws.onopen);
      });

      const response = await api.post('/xml-download/download-by-filters', {
        filters: {
          dataInicio: dataInicio || null,
          dataFim: dataFim || null,
          modelo: modelo.length > 0 ? modelo : null,
          cfop: cfopList.length > 0 ? cfopList : null,
          direcao: direcao || null,
          status: status || null,
        },
      });

      if (response.data.success) {
        const oid = response.data.outputId;
        setOutputId(oid);
        outputIdRef.current = oid;
        addLog('Solicitação enviada. Aguardando processamento...', 'success');
        // Polling de fallback: se WS perder a mensagem (tarefa muito rápida)
        startFallbackPoll(oid);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao iniciar processamento');
      setIsProcessing(false);
      if (wsRef.current) wsRef.current.close();
    }
  };

  const downloadZip = async () => {
    const oid = outputId || outputIdRef.current;
    if (!oid) return;
    setDlState({ progress: 0, done: false, rate: 0 });
    let lastLoaded = 0;
    let lastTime = Date.now();
    try {
      const response = await api.get(
        `/xml-download/download-filter-zip?outputId=${oid}`,
        {
          responseType: 'blob',
          timeout: 0,
          onDownloadProgress: (e) => {
            const now = Date.now();
            const elapsed = (now - lastTime) / 1000;
            let rate = 0;
            if (elapsed >= 0.2) {
              rate = (e.loaded - lastLoaded) / elapsed / (1024 * 1024);
              lastLoaded = e.loaded;
              lastTime = now;
            }
            const progress = e.total > 0 ? Math.round((e.loaded / e.total) * 100) : Math.min(99, Math.round(e.loaded / (1024 * 1024)));
            setDlState({ progress, done: false, rate: parseFloat(rate.toFixed(1)) });
          },
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `xmls_${oid}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setDlState({ progress: 100, done: true, rate: 0 });
      toast.success('Download concluído!');
      setTimeout(() => setDlState(null), 4000);
    } catch {
      toast.error('Erro ao baixar arquivo');
      setDlState(null);
    }
  };

  useEffect(() => {
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, []);

  const filterDownloadButtons = (
    <>
      {dlState && (
        <>
          <DlProgressWrap $p={dlState.progress} $done={dlState.done} />
          <DlLabel>
            <span>
              {dlState.done ? 'Download concluído!' :
                dlState.rate > 0 ? `Baixando... ${dlState.rate} MB/s` : 'Aguardando...'}
            </span>
            <span>{dlState.done ? <FiCheckCircle style={{ color: '#10b981' }} /> : `${dlState.progress}%`}</span>
          </DlLabel>
        </>
      )}
      <BtnsRow>
        <DlBtn $primary disabled={!!dlState && !dlState.done} onClick={downloadZip}>
          <FiDownload /> {dlState && !dlState.done ? `${dlState.progress}%` : 'Baixar Pacote'}
        </DlBtn>
      </BtnsRow>
    </>
  );

  if (isProcessing || processingComplete) {
    return (
      <XmlDownloadTaskLog
        isProcessing={isProcessing}
        processingComplete={processingComplete}
        taskProgress={taskProgress}
        currentStage={currentStage}
        taskStats={taskStats}
        taskLogs={taskLogs}
        downloadButtons={filterDownloadButtons}
      />
    );
  }

  return (
    <>
      <FilterGroup>
        <FilterSectionHeader>
          <FiCalendar />
          Período
        </FilterSectionHeader>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.7em' }}>
          <div>
            <FilterLabel>De</FilterLabel>
            <FilterInput
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
            />
          </div>
          <div>
            <FilterLabel>Até</FilterLabel>
            <FilterInput
              type="date"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
            />
          </div>
        </div>
      </FilterGroup>

      <FilterGroup>
        <FilterSectionHeader>
          <FiFileText />
          Tipo de Documento
        </FilterSectionHeader>
        <FilterBadges>
          {[{ label: 'NF-e', value: '55' }, { label: 'CT-e', value: '57' }].map(({ label, value }) => (
            <FilterBadge
              key={value}
              $active={modelo.includes(value)}
              onClick={() => toggleModelo(value)}
            >
              {label}
            </FilterBadge>
          ))}
          {modelo.length > 0 && (
            <FilterBadge onClick={() => setModelo([])}>
              Limpar
            </FilterBadge>
          )}
        </FilterBadges>
      </FilterGroup>

      <FilterGroup>
        <FilterSectionHeader>
          <MdOutlineNumbers />
          CFOP
        </FilterSectionHeader>
        <FilterInput
          ref={cfopInputRef}
          type="text"
          placeholder="Digite ou pesquise um CFOP (ex: 5102)"
          value={cfopInput}
          onChange={(e) => handleCfopInput(e.target.value)}
          onKeyDown={handleCfopKeyDown}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
          onFocus={() => cfopInput.length >= 1 && setShowSuggestions(true)}
        />
        {showSuggestions && cfopSuggestions.length > 0 && (
          <SuggestionsContainer>
            {cfopSuggestions.map((c) => (
              <SuggestionItem key={c.codigo} onMouseDown={() => addCfop(c.codigo)}>
                <div className="suggestion-icon">
                  <MdOutlineNumbers />
                </div>
                <div className="suggestion-info">
                  <span className="suggestion-name">{c.codigo}</span>
                  <span className="suggestion-cnpj">{c.descricao}</span>
                </div>
              </SuggestionItem>
            ))}
          </SuggestionsContainer>
        )}
        {cfopList.length > 0 && (
          <FilterBadges style={{ marginTop: '0.6em' }}>
            {cfopList.map((c) => (
              <FilterBadge key={c} $active onClick={() => removeCfop(c)}>
                {c} <FiX style={{ marginLeft: '0.3em', fontSize: '0.85em' }} />
              </FilterBadge>
            ))}
          </FilterBadges>
        )}
      </FilterGroup>

      <FilterGroup>
        <FilterSectionHeader>
          <FiRepeat />
          Direção
        </FilterSectionHeader>
        <FilterBadges>
          {[
            { label: 'Emissão Própria', value: 'propria' },
            { label: 'Terceiros', value: 'terceiros' },
          ].map(({ label, value }) => (
            <FilterBadge
              key={value}
              $active={direcao === value}
              onClick={() => setDirecao(direcao === value ? null : value)}
            >
              {label}
            </FilterBadge>
          ))}
        </FilterBadges>
      </FilterGroup>

      <FilterGroup>
        <FilterSectionHeader>
          <FiCheckCircle />
          Status
        </FilterSectionHeader>
        <FilterBadges>
          {['Autorizada', 'Cancelada'].map((s) => (
            <FilterBadge
              key={s}
              $active={status === s}
              onClick={() => setStatus(status === s ? null : s)}
            >
              {s}
            </FilterBadge>
          ))}
        </FilterBadges>
      </FilterGroup>

      <SubmitButton disabled={!hasFilters} onClick={handleSubmit}>
        <FiSearch />
        Buscar e Baixar XMLs
      </SubmitButton>
    </>
  );
}
